import { useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  LinearProgress,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import {
  ALLOWED_DOCUMENT_MIME_TYPES,
  MAX_DOCUMENT_FILE_SIZE_BYTES,
} from "@/api/labels";
import { useCompleteVersion, useInitiateVersion } from "@/api/hooks/documents";
import { computeSha256, uploadToPresignedUrl } from "@/api/upload";
import type { DocumentValidation } from "@/api/types";
import { DelegationLogReviewPanel } from "./DelegationLogReviewPanel";
import { DocumentValidationsSection } from "./DocumentValidationsSection";
import { GcpCertReviewPanel } from "./GcpCertReviewPanel";
import { RotationReviewPanel } from "./RotationReviewPanel";

const ALLOWED_TYPES = new Set<string>(ALLOWED_DOCUMENT_MIME_TYPES);

type Stage =
  | "idle"
  | "initiating"
  | "uploading"
  | "completing"
  | "review" // waiting for operator to override FAILED validations
  | "done";

interface Props {
  documentId: string;
  /** Called with the new versionId after the upload completes — only
   * fired once all validations are PASSED / OVERRIDDEN. If any
   * validation comes back FAILED, the form stays in "review" state and
   * the parent doesn't get a callback until the operator overrides. */
  onComplete: (versionId: string) => void;
  onCancel: () => void;
}

/** Adds a new version to an existing Document. Distinct from
 * DocumentUploadForm (which creates the Document itself): this hits
 * /api/documents/{id}/versions and requires a reason in the audit log
 * for every new version. */
export function NewDocumentVersionForm({ documentId, onComplete, onCancel }: Props) {
  const initiate = useInitiateVersion(documentId);
  const complete = useCompleteVersion();

  const [reason, setReason] = useState("");
  const [file, setFile] = useState<File | null>(null);
  const [stage, setStage] = useState<Stage>("idle");
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [completedVersionId, setCompletedVersionId] = useState<string | null>(
    null,
  );
  const [completedVersionMeta, setCompletedVersionMeta] = useState<{
    content_type: string;
    version_number: number;
  } | null>(null);
  const [validations, setValidations] = useState<DocumentValidation[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFile = (f: File | null) => {
    setError(null);
    if (!f) {
      setFile(null);
      return;
    }
    if (!ALLOWED_TYPES.has(f.type)) {
      setError(`Unsupported file type: ${f.type || "unknown"}`);
      setFile(null);
      return;
    }
    if (f.size > MAX_DOCUMENT_FILE_SIZE_BYTES) {
      setError(`File too large (max ${MAX_DOCUMENT_FILE_SIZE_BYTES / 1024 / 1024} MB)`);
      setFile(null);
      return;
    }
    setFile(f);
  };

  const submit = async () => {
    if (!file || !reason.trim()) return;
    setError(null);
    try {
      setStage("initiating");
      setProgress(0);
      const presigned = await initiate.mutateAsync({
        content_type: file.type,
        file_name: file.name,
        reason: reason.trim(),
      });

      setStage("uploading");
      const sha256 = await computeSha256(file);
      await uploadToPresignedUrl(
        file,
        presigned.upload_url,
        presigned.required_headers,
        (fraction) => setProgress(fraction),
      );

      setStage("completing");
      const result = await complete.mutateAsync({
        documentId,
        versionId: presigned.version_id,
        payload: { sha256, file_size_bytes: file.size },
      });

      setCompletedVersionId(presigned.version_id);
      setCompletedVersionMeta({
        content_type: result.version.content_type,
        version_number: result.version.version_number,
      });
      setValidations(result.validations);
      if (
        result.validations.some(
          (v) =>
            v.status === "failed" ||
            v.status === "needs_review" ||
            v.status === "error",
        )
      ) {
        // Don't dismiss — operator needs to review, override, or see
        // what went wrong. ERROR rows are surfaced via the validations
        // section so the user isn't left on the main screen with no
        // indication that extraction silently failed.
        setStage("review");
        return;
      }
      setStage("done");
      onComplete(presigned.version_id);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Upload failed");
      setStage("idle");
    }
  };

  // Called by RotationReviewPanel after the operator confirms per-page
  // rotations. The backend created v2 + re-ran content validators on
  // it (orientation skipped — we just confirmed it). We pivot the form
  // to that v2: subsequent review-panel branches now operate on the
  // corrected file. If v2's validators all pass, we close the drawer.
  const onRotationApplied = (
    newVersion: import("@/api/types").DocumentVersion,
    nextValidations: DocumentValidation[],
  ) => {
    setCompletedVersionId(newVersion.id);
    setCompletedVersionMeta({
      content_type: newVersion.content_type,
      version_number: newVersion.version_number,
    });
    setValidations(nextValidations);
    const stillBlocking = nextValidations.some(
      (v) =>
        v.status === "failed" ||
        v.status === "needs_review" ||
        v.status === "error",
    );
    if (stillBlocking) {
      // Stay in "review" stage — the priority chain below picks the
      // next panel (delegation log, GCP, override pills, …).
      return;
    }
    setStage("done");
    onComplete(newVersion.id);
  };

  // After an override/confirm, the validation row's status flips and
  // the documents query cache is invalidated. We update our local copy
  // and decide whether to close.
  const onValidationsResolved = (next: DocumentValidation[]) => {
    setValidations(next);
    const stillBlocking = next.some(
      (v) =>
        v.status === "failed" ||
        v.status === "needs_review" ||
        v.status === "error",
    );
    if (!stillBlocking && completedVersionId) {
      setStage("done");
      onComplete(completedVersionId);
    }
  };

  // Priority: orientation review first (every other validator ran on
  // a potentially rotated file, so addressing rotation first is the
  // sane order). Then delegation log (per-location-study roster).
  // Then GCP / other extract-and-confirm validators. Then FAILED
  // override pills.
  const orientationNeedsReview = validations.find(
    (v) => v.validator_id === "pdf.orientation_check" && v.status === "needs_review",
  );
  const delegationLogNeedsReview = validations.find(
    (v) => v.validator_id === "delegation_log.review" && v.status === "needs_review",
  );
  const otherNeedsReview = validations.find(
    (v) =>
      v.validator_id !== "pdf.orientation_check" &&
      v.validator_id !== "delegation_log.review" &&
      v.status === "needs_review",
  );

  const busy = stage === "initiating" || stage === "uploading" || stage === "completing";
  const inReview = stage === "review";

  if (inReview && orientationNeedsReview && completedVersionId) {
    return (
      <RotationReviewPanel
        documentId={documentId}
        versionId={completedVersionId}
        validation={orientationNeedsReview}
        onApplied={onRotationApplied}
        onCancel={onCancel}
      />
    );
  }

  if (
    inReview &&
    delegationLogNeedsReview &&
    completedVersionId &&
    completedVersionMeta
  ) {
    return (
      <DelegationLogReviewPanel
        documentId={documentId}
        versionId={completedVersionId}
        contentType={completedVersionMeta.content_type}
        versionLabel={`v${completedVersionMeta.version_number}`}
        validation={delegationLogNeedsReview}
        onConfirmed={(updated) =>
          onValidationsResolved(
            validations.map((v) => (v.id === updated.id ? updated : v)),
          )
        }
        onCancel={onCancel}
      />
    );
  }

  if (inReview && otherNeedsReview && completedVersionId && completedVersionMeta) {
    return (
      <Stack spacing={2}>
        <Alert severity="info">
          Review the extracted fields against the document on the left.
          Correct anything Bedrock got wrong, then confirm to record the
          credential.
        </Alert>
        <GcpCertReviewPanel
          documentId={documentId}
          versionId={completedVersionId}
          contentType={completedVersionMeta.content_type}
          versionLabel={`v${completedVersionMeta.version_number}`}
          validation={otherNeedsReview}
          onConfirmed={(updated) =>
            onValidationsResolved(
              validations.map((v) => (v.id === updated.id ? updated : v)),
            )
          }
          onCancel={onCancel}
        />
      </Stack>
    );
  }

  if (inReview) {
    return (
      <Stack spacing={2}>
        <Alert severity="warning">
          Upload completed, but one or more validations failed. Review
          the issue below — either mark it reviewed with a reason, or
          cancel and upload a corrected file.
        </Alert>
        <DocumentValidationsSection
          documentId={documentId}
          validations={validations}
          onOverridden={(updated) =>
            onValidationsResolved(
              validations.map((v) => (v.id === updated.id ? updated : v)),
            )
          }
        />
        <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1 }}>
          <Button onClick={onCancel}>Done</Button>
        </Box>
      </Stack>
    );
  }

  return (
    <Stack spacing={2}>
      {error && <Alert severity="error">{error}</Alert>}

      <TextField
        label="Reason for replacement"
        value={reason}
        onChange={(e) => setReason(e.target.value)}
        required
        multiline
        minRows={2}
        helperText="Required — recorded on the audit log for this version."
      />

      <Box
        onDragOver={(e) => e.preventDefault()}
        onDrop={(e) => {
          e.preventDefault();
          handleFile(e.dataTransfer.files?.[0] ?? null);
        }}
        onClick={() => fileInputRef.current?.click()}
        sx={{
          border: 2,
          borderStyle: "dashed",
          borderColor: "divider",
          borderRadius: 2,
          py: 3,
          textAlign: "center",
          cursor: "pointer",
          bgcolor: "background.default",
        }}
      >
        {file ? (
          <Typography>
            <strong>{file.name}</strong> · {(file.size / 1024 / 1024).toFixed(2)} MB
          </Typography>
        ) : (
          <Typography color="text.secondary">
            Drag a file here, or click to choose
          </Typography>
        )}
        <input
          ref={fileInputRef}
          type="file"
          hidden
          accept={ALLOWED_DOCUMENT_MIME_TYPES.join(",")}
          onChange={(e) => handleFile(e.target.files?.[0] ?? null)}
        />
      </Box>

      {busy && (
        <Box>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            {stage === "initiating" && "Preparing upload…"}
            {stage === "uploading" && `Uploading… ${Math.round(progress * 100)}%`}
            {stage === "completing" && "Finalizing…"}
          </Typography>
          <LinearProgress
            variant={stage === "uploading" ? "determinate" : "indeterminate"}
            value={progress * 100}
          />
        </Box>
      )}

      <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1 }}>
        <Button onClick={onCancel} disabled={busy}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={submit}
          disabled={!file || !reason.trim() || busy}
          startIcon={busy ? <CircularProgress size={16} /> : null}
        >
          Add version
        </Button>
      </Box>
    </Stack>
  );
}
